import { useCallback } from 'react';
import { useLazyQuery, useMutation } from '@apollo/client';

import { ACTIVATE_PATIENT } from '@/apollo/mutations';
import { MY_CARETEAM_QUERY } from '@/apollo/queries';
import { Product, getPdtUid } from '@/utils/productHelpers';
import { logError } from '@/utils/logError';

export type ActivationOutcome =
  | { action: 'continue' }
  | { action: 'coverage'; orgSlug: string }
  | { action: 'error' };

/**
 * Activates the care team when the screen that normally does it was skipped — by
 * dependents, or by anyone returning through login. Pass `knownOrgSlug` when the caller
 * already has it (it travels in the sign-up URL) to skip the lookup.
 */
export const useEnsureActivated = (product: Product) => {
  const [getMyCareteam] = useLazyQuery(MY_CARETEAM_QUERY, { fetchPolicy: 'no-cache' });
  const [activatePatient] = useMutation(ACTIVATE_PATIENT);

  return useCallback(async (
    accessToken: string,
    knownOrgSlug?: string | null,
  ): Promise<ActivationOutcome> => {
    const pdtUid = getPdtUid(product);
    const context = { headers: { Authorization: `Bearer ${accessToken}` } };
    let orgSlug = knownOrgSlug ?? undefined;

    if (!orgSlug) {
      let hasDetails = false;
      try {
        const { data } = await getMyCareteam({ variables: { pdtUid }, context });
        if (!data?.myCareteam) {
          logError('[useEnsureActivated]', 'no care team for this user and PDT', undefined, {
            level: 'warning',
            extra: { pdtUid },
          });
          return { action: 'continue' };
        }
        if (data.myCareteam.enrolledTime) {
          return { action: 'continue' };
        }
        orgSlug = data.myCareteam.userPdtData?.organizationSlug ?? undefined;
        // A date of birth means their details were captured — by caregiver
        // at enrollment, or by the coverage form itself.
        hasDetails = Boolean(data.myCareteam.patient?.user?.dateOfBirth);
      } catch (err) {
        logError('[useEnsureActivated]', 'myCareteam lookup failed', err);
        return { action: 'continue' };
      }

      // Without the organization we can neither activate nor route, so leave the user as
      // they are today rather than blocking them on something a retry would not fix.
      if (!orgSlug) {
        logError('[useEnsureActivated]', 'care team has no organization slug', undefined, {
          level: 'warning',
          extra: { pdtUid },
        });
        return { action: 'continue' };
      }

      if (!hasDetails) {
        return { action: 'coverage', orgSlug };
      }
    }

    try {
      const response = await activatePatient({
        variables: { pdtUid, organizationSlug: orgSlug },
        context,
      });
      if (!response.data?.activatePatient?.success) {
        throw new Error('activatePatient returned success=false');
      }
    } catch (err) {
      logError('[useEnsureActivated]', 'activatePatient failed', err);
      return { action: 'error' };
    }

    return { action: 'continue' };
  }, [product, getMyCareteam, activatePatient]);
};
